/* Bastar Arts — global.js
   Lightweight, dependency-free utilities and custom elements.
   Events are namespaced `ba:` to avoid conflicts with apps. */
(function () {
  'use strict';

  /* ---------- Utilities ---------- */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  window.BA = window.BA || {};

  BA.announce = function (message) {
    const region = document.querySelector('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 50);
  };

  BA.toast = function (message) {
    let toast = document.querySelector('.ba-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'ba-toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toast._timer);
    toast._timer = window.setTimeout(() => toast.classList.remove('is-visible'), 3200);
  };

  BA.trapFocus = function (container, event) {
    const focusable = Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ---------- Overlay (shared) ---------- */
  function getOverlay() {
    let overlay = document.querySelector('.ba-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'ba-overlay';
      document.body.appendChild(overlay);
    }
    return overlay;
  }

  /* ---------- Drawer / dialog controller ---------- */
  class BADrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-drawer-panel]') || this;
      this._keydown = this._keydown.bind(this);
      this.querySelectorAll('[data-drawer-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));
      document.querySelectorAll(`[data-drawer-open="${this.id}"]`).forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(btn);
        });
      });
    }
    open(opener) {
      this._opener = opener || document.activeElement;
      this.panel.classList.add('is-open');
      const overlay = getOverlay();
      overlay.classList.add('is-active');
      overlay.addEventListener('click', this._overlayClose = () => this.close(), { once: true });
      document.addEventListener('keydown', this._keydown);
      document.body.style.overflow = 'hidden';
      if (this._opener && this._opener.setAttribute) this._opener.setAttribute('aria-expanded', 'true');
      const target = this.panel.querySelector('[autofocus]') || this.panel.querySelector(FOCUSABLE);
      window.setTimeout(() => target && target.focus(), 60);
      this.dispatchEvent(new CustomEvent('ba:drawer:open', { bubbles: true }));
    }
    close() {
      this.panel.classList.remove('is-open');
      getOverlay().classList.remove('is-active');
      document.removeEventListener('keydown', this._keydown);
      document.body.style.overflow = '';
      if (this._opener && this._opener.setAttribute) {
        this._opener.setAttribute('aria-expanded', 'false');
        if (this._opener.focus) this._opener.focus();
      }
      this.dispatchEvent(new CustomEvent('ba:drawer:close', { bubbles: true }));
    }
    get isOpen() { return this.panel.classList.contains('is-open'); }
    _keydown(e) {
      if (e.key === 'Escape') this.close();
      if (e.key === 'Tab') BA.trapFocus(this.panel, e);
    }
  }
  customElements.define('ba-drawer', BADrawer);

  /* ---------- Sticky / transparent header ---------- */
  const headerWrapper = document.querySelector('[data-header-wrapper]');
  if (headerWrapper) {
    let lastKnown = 0;
    let ticking = false;
    const update = () => {
      headerWrapper.classList.toggle('is-scrolled', lastKnown > 24);
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      lastKnown = window.scrollY;
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* Close header dropdowns on outside click / Escape */
  document.addEventListener('click', (e) => {
    document.querySelectorAll('.ba-header__nav details[open]').forEach((details) => {
      if (!details.contains(e.target)) details.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('.ba-header__nav details[open]').forEach((details) => {
      details.removeAttribute('open');
      const summary = details.querySelector('summary');
      if (summary) summary.focus();
    });
  });

  /* ---------- Hero slideshow ---------- */
  class BASlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (this.slides.length <= 1) return;
      this.dots = Array.from(this.querySelectorAll('[data-slide-dot]'));
      this.index = 0;
      this.autoplay = this.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.interval = parseInt(this.dataset.interval || '5500', 10);
      const prev = this.querySelector('[data-slide-prev]');
      const next = this.querySelector('[data-slide-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1, true));
      if (next) next.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));
      this.addEventListener('mouseenter', () => this._pause());
      this.addEventListener('mouseleave', () => this._resume());
      this.addEventListener('focusin', () => this._pause());
      this.addEventListener('focusout', () => this._resume());
      document.addEventListener('visibilitychange', () => document.hidden ? this._pause() : this._resume());
      this._start();
      this.go(0);
    }
    disconnectedCallback() { this._pause(); }
    go(index, user) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.querySelectorAll(FOCUSABLE).forEach((el) => el.tabIndex = active ? 0 : -1);
      });
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
      if (user) {
        this._pause();
        window.setTimeout(() => this._resume(), 12000);
      }
    }
    _start() {
      if (!this.autoplay) return;
      this._timer = window.setInterval(() => this.go(this.index + 1), this.interval);
    }
    _pause() { window.clearInterval(this._timer); this._timer = null; }
    _resume() { if (!this._timer) this._start(); }
  }
  customElements.define('ba-slideshow', BASlideshow);

  /* ---------- Tabs (craft process) ---------- */
  class BATabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          let target = null;
          if (e.key === 'ArrowRight') target = (i + 1) % this.tabs.length;
          if (e.key === 'ArrowLeft') target = (i - 1 + this.tabs.length) % this.tabs.length;
          if (target !== null) {
            e.preventDefault();
            this.select(target);
            this.tabs[target].focus();
          }
        });
      });
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((panel, i) => panel.hidden = i !== index);
    }
  }
  customElements.define('ba-tabs', BATabs);

  /* ---------- Quantity input ---------- */
  class BAQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) return;
        e.preventDefault();
        const step = btn.dataset.qty === 'plus' ? 1 : -1;
        const min = parseInt(this.input.min || '1', 10);
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || min) + step));
        if (next !== parseInt(this.input.value, 10)) {
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }
  customElements.define('ba-quantity', BAQuantity);

  /* ---------- Trace Your Art lookup ----------
     Fetches a provenance record from the configured endpoint (Google Apps
     Script) as ?code=<value> and fills the pre-rendered certificate slots with
     textContent only. Artisan details are merged from three sources, in order
     of precedence: the endpoint response, then the section's local artisan
     directory (matched on artisan id), so the maker still appears when the
     sheet carries nothing but an ID. Falls back to an embedded story map when
     no endpoint is set. All values are untrusted data — never innerHTML. */
  class BATrace extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="code"], input');
      this.result = this.querySelector('[data-trace-result]');
      this.skeleton = this.querySelector('[data-trace-skeleton]');
      this.statusEl = this.querySelector('[data-trace-status]');
      this.submitBtn = this.form && this.form.querySelector('[type="submit"]');
      this.submitLabel = this.querySelector('[data-trace-submit-label]');
      this.endpoint = (this.dataset.endpoint || '').trim();
      this.stories = this._json('[data-trace-data]');
      this.directory = this._json('[data-trace-artisans]');
      if (!this.form) return;

      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.lookup(); });

      const reset = this.querySelector('[data-trace-reset]');
      if (reset) reset.addEventListener('click', () => this.reset());

      const copy = this.querySelector('[data-trace-copy]');
      if (copy) copy.addEventListener('click', () => this.copyCode());

      /* Deep link: /pages/trace?code=BA-… (QR codes on the packaging) */
      const deepLink = new URLSearchParams(window.location.search).get('code');
      if (deepLink) { this.input.value = deepLink; this.lookup({ silent: true }); }
    }

    _json(selector) {
      const el = this.querySelector(selector);
      if (!el) return {};
      try { return JSON.parse(el.textContent) || {}; } catch (err) { return {}; }
    }

    reset() {
      this.result.hidden = true;
      this.setStatus('');
      this.input.value = '';
      this.input.focus();
    }

    setStatus(message, type) {
      if (!this.statusEl) return;
      this.statusEl.textContent = message || '';
      this.statusEl.hidden = !message;
      this.statusEl.dataset.state = type || '';
      if (message) BA.announce(message);
    }

    setLoading(isLoading) {
      if (this.submitBtn) {
        this.submitBtn.disabled = isLoading;
        this.submitBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }
      if (this.submitLabel) {
        if (this._defaultLabel == null) this._defaultLabel = this.submitLabel.textContent;
        this.submitLabel.textContent = isLoading
          ? (this.dataset.loadingLabel || 'Tracing…')
          : this._defaultLabel;
      }
      if (this.skeleton) this.skeleton.hidden = !isLoading;
      if (isLoading) this.result.hidden = true;
    }

    async copyCode() {
      const value = this.querySelector('[data-field="code"]');
      const flash = this.querySelector('[data-trace-copy-flash]');
      if (!value || !navigator.clipboard) return;
      try {
        await navigator.clipboard.writeText(value.textContent.trim());
        if (flash) {
          flash.textContent = this.dataset.copiedLabel || 'Copied';
          flash.setAttribute('data-visible', '');
          clearTimeout(this._flashTimer);
          this._flashTimer = setTimeout(() => flash.removeAttribute('data-visible'), 1800);
        }
      } catch (err) { /* clipboard blocked — nothing to do */ }
    }

    async lookup(options) {
      const silent = options && options.silent;
      const code = (this.input.value || '').trim().toUpperCase().replace(/\s+/g, '');
      if (!code) {
        if (!silent) this.setStatus(this.dataset.emptyMessage, 'error');
        return;
      }
      this.input.value = code;

      if (!this.endpoint) { this.lookupFallback(code); return; }

      this.setLoading(true);
      this.setStatus('');
      try {
        const url = this.endpoint + (this.endpoint.indexOf('?') === -1 ? '?' : '&') + 'code=' + encodeURIComponent(code);
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        const data = await res.json();
        if (!data || data.ok === false) {
          this.result.hidden = true;
          this.setStatus((data && data.error) || this.dataset.notfoundMessage, 'error');
          return;
        }
        this.render(Object.assign({ code: code }, data));
        this.setStatus('');
      } catch (err) {
        this.result.hidden = true;
        this.setStatus(this.dataset.errorMessage, 'error');
      } finally {
        this.setLoading(false);
      }
    }

    /* Offline / no-endpoint fallback using the embedded story map */
    lookupFallback(code) {
      const upper = code.toUpperCase();
      let hit = this.stories[upper];
      if (!hit) {
        const entry = Object.entries(this.stories).find(([prefix]) => upper.indexOf(prefix) === 0);
        hit = entry && entry[1];
      }
      if (hit) {
        this.render(Object.assign({ code: upper }, hit));
        this.setStatus('');
      } else {
        this.result.hidden = true;
        this.setStatus(this.dataset.notfoundMessage, 'error');
      }
    }

    /* Resolve a dotted path (e.g. "artisan.name") against the data object */
    static resolve(obj, path) {
      return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
    }

    /* First non-empty value among the given keys of an object */
    static pick(obj, keys) {
      for (let i = 0; i < keys.length; i += 1) {
        const v = obj[keys[i]];
        if (v != null && String(v).trim() !== '') return String(v).trim();
      }
      return '';
    }

    render(raw) {
      const data = Object.assign({}, raw, { artisan: this._artisan(raw.artisan) });

      /* Dates arrive either pre-formatted or as ISO strings — normalise both */
      data.made_on = this._date(data.made_on);
      data.received_at = this._date(data.received_at);

      /* Text + image slots */
      this.result.querySelectorAll('[data-field]').forEach((el) => {
        const value = BATrace.resolve(data, el.dataset.field);
        if (el.tagName === 'IMG') {
          if (value) { el.src = value; el.hidden = false; }
          else { el.removeAttribute('src'); el.hidden = true; }
          const ph = el.parentElement.querySelector('[data-field-placeholder]');
          if (ph) ph.hidden = !!value;
        } else {
          el.textContent = value == null ? '' : String(value);
        }
      });

      /* Hide rows / pills / journey stops whose value is empty */
      this.result.querySelectorAll('[data-fact]').forEach((el) => {
        const value = BATrace.resolve(data, el.dataset.fact);
        el.hidden = value == null || String(value).trim() === '';
      });

      /* Optional product link */
      this.result.querySelectorAll('[data-field-link]').forEach((el) => {
        const value = BATrace.resolve(data, el.dataset.fieldLink);
        if (value) { el.href = value; el.hidden = false; } else { el.hidden = true; }
      });

      /* Artisan card: show whenever we know anything, and explain the gap when
         all we have is an ID that isn't in the directory yet. */
      const card = this.result.querySelector('[data-trace-artisan]');
      if (card) {
        const a = data.artisan;
        const named = ['name', 'location', 'craft', 'experience', 'story', 'photo']
          .some((k) => a[k] && String(a[k]).trim() !== '');
        card.hidden = !named && !a.artisan_id;
        const pending = card.querySelector('[data-trace-artisan-pending]');
        if (pending) pending.hidden = named;

        /* No portrait yet? Fall back to the maker's initial before the generic icon */
        const initial = card.querySelector('[data-trace-artisan-initial]');
        const icon = card.querySelector('.ba-cert__artisan-placeholder');
        if (initial) {
          const useInitial = !a.photo && !!a.name;
          initial.textContent = useInitial ? a.name.trim().charAt(0).toUpperCase() : '';
          initial.hidden = !useInitial;
          if (icon) icon.hidden = !!a.photo || useInitial;
        }
      }

      /* Hide the journey rail if no stop survived */
      const journey = this.result.querySelector('[data-trace-journey]');
      if (journey) {
        journey.hidden = !Array.from(journey.querySelectorAll('[data-fact]')).some((el) => !el.hidden);
      }

      this.result.hidden = false;
      this.result.scrollIntoView({ behavior: 'smooth', block: 'start' });
      BA.announce((data.product_title || '') + ' ' + (data.craft_type || ''));
    }

    /* Normalise varied sheet column names, then fill gaps from the section's
       local artisan directory (endpoint values always win). */
    _artisan(source) {
      const a = source || {};
      const merged = {
        name: BATrace.pick(a, ['name', 'artisan_name', 'artisan', 'maker']),
        location: BATrace.pick(a, ['location', 'village', 'cluster', 'region', 'place', 'district']),
        craft: BATrace.pick(a, ['craft', 'craft_type', 'speciality', 'specialty']),
        experience: BATrace.pick(a, ['experience', 'years_experience', 'years', 'exp']),
        story: BATrace.pick(a, ['story', 'bio', 'about']),
        artisan_id: BATrace.pick(a, ['artisan_id', 'id', 'code']),
        photo: BATrace.pick(a, ['photo', 'image', 'photo_url', 'primary_photo', 'url'])
      };

      const entry = merged.artisan_id && this.directory[merged.artisan_id.toUpperCase()];
      if (entry) {
        Object.keys(merged).forEach((key) => {
          if (!merged[key] && entry[key]) merged[key] = String(entry[key]).trim();
        });
      }

      merged.experience = this._experience(merged.experience);
      return merged;
    }

    _experience(value) {
      const v = String(value || '').trim();
      if (!v) return '';
      if (/exper|year/i.test(v)) return v;
      if (/^\d+$/.test(v)) return v + ' years experience';
      return v;
    }

    /* "2025-08-24T18:30:00.000Z" -> "25 Aug 2025"; anything else passes through */
    _date(value) {
      const v = String(value == null ? '' : value).trim();
      if (!/^\d{4}-\d{2}-\d{2}/.test(v)) return v;
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      try {
        return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(d);
      } catch (err) { return v; }
    }
  }
  customElements.define('ba-trace', BATrace);

  /* ---------- Newsletter success state ---------- */
  document.querySelectorAll('[data-newsletter-form]').forEach((wrapper) => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') === 'true' && wrapper.querySelector('[data-newsletter-success]')) {
      wrapper.querySelector('[data-newsletter-success]').hidden = false;
      const btn = wrapper.querySelector('button[type="submit"]');
      if (btn) btn.textContent = btn.dataset.successLabel || btn.textContent;
    }
  });

  /* ---------- Scroll reveal ---------- */
  if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });
    document.querySelectorAll('.ba-reveal').forEach((el) => io.observe(el));
  } else {
    document.querySelectorAll('.ba-reveal').forEach((el) => el.classList.add('is-revealed'));
  }

  /* ---------- Back to top ---------- */
  const backToTop = document.querySelector('[data-back-to-top]');
  if (backToTop) {
    let btTicking = false;
    window.addEventListener('scroll', () => {
      if (btTicking) return;
      btTicking = true;
      window.requestAnimationFrame(() => {
        backToTop.hidden = window.scrollY < window.innerHeight * 1.5;
        btTicking = false;
      });
    }, { passive: true });
    backToTop.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  /* ---------- Collection description expander ---------- */
  document.querySelectorAll('[data-desc-toggle]').forEach((btn) => {
    const target = document.getElementById(btn.getAttribute('aria-controls'));
    if (!target) return;
    if (target.scrollHeight <= target.clientHeight + 4) { btn.hidden = true; target.classList.remove('is-collapsed'); return; }
    btn.addEventListener('click', () => {
      const collapsed = target.classList.toggle('is-collapsed');
      btn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      btn.textContent = collapsed ? btn.dataset.moreLabel : btn.dataset.lessLabel;
    });
  });

  /* ---------- Share ---------- */
  document.querySelectorAll('[data-share-native]').forEach((btn) => {
    if (!navigator.share) { btn.hidden = true; return; }
    btn.addEventListener('click', () => {
      navigator.share({ title: document.title, url: btn.dataset.shareUrl || window.location.href }).catch(() => {});
    });
  });
  document.querySelectorAll('[data-share-copy]').forEach((btn) => {
    btn.addEventListener('click', () => {
      navigator.clipboard.writeText(btn.dataset.shareUrl || window.location.href).then(() => BA.toast(btn.dataset.copiedLabel || 'Link copied'));
    });
  });

  /* ---------- Predictive search ---------- */
  class BAPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', this._debounce(() => this.search(), 300));
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.resetResults(); });
    }
    _debounce(fn, wait) {
      let t;
      return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
    }
    search() {
      const query = this.input.value.trim();
      if (query.length < 2) { this.resetResults(); return; }
      const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article&resources[limit]=4&section_id=predictive-search`;
      fetch(url)
        .then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const inner = html.querySelector('#shopify-section-predictive-search');
          this.results.innerHTML = inner ? inner.innerHTML : '';
          this.results.hidden = false;
          const count = this.results.querySelectorAll('a').length;
          BA.announce(count + ' suggestions');
        })
        .catch(() => this.resetResults());
    }
    resetResults() { this.results.innerHTML = ''; this.results.hidden = true; }
  }
  customElements.define('ba-predictive-search', BAPredictiveSearch);
})();
